import 'reflect-metadata';
import { container } from 'tsyringe';
import { ABTestingService } from '../../services/ABTestingService/ABTestingService';
import { AnalyticsService } from '../../services/AnalyticsService';
import { ApplicationServiceFactory } from '../../services/applicationService/ApplicationServiceFactory';
import { AuthProviderService } from '../../services/authProviderService';
import { AuthTokenService } from '../../services/authTokenService';
import { GetAccessTokenService } from '../../services/authTokenService/getAcessTokenService';
import { BackgroundTaskManagerService } from '../../services/backgroundTaskService';
import { SystemJSAssetLoaderService } from '../../services/backgroundTaskService/assetLoader';
import { BackgroundTaskService } from '../../services/backgroundTaskService/backgroundTask';
import BreadcrumbService from '../../services/breadcrumbService/BreadcrumbService';
import { ConsentService } from '../../services/consentService';
import { EventServiceFactory } from '../../services/eventService/EventServiceFactory';
import WebStoreService from '../../services/storeService/webStoreService/WebStoreService';
import FallbackService from '../../services/fallbackService/FallbackService';
import { LaunchDarklyFeatureFlagService } from '../../services/featureFlagService';
import { GrantService } from '../../services/grants/GrantService';
import { GrantHistoryService } from '../../services/grants/grantsHistory';
import { GraphQLServiceFactory } from '../../services/graphQLService/GraphQLServiceFactory';
import { IdleLogoutService, IdleService } from '../../services/IdleService';
import { LayoutsService } from '../../services/LayoutsService';
import {
  LocalizationService,
  LocalizationTranslatorService
} from '../../services/localizationService';
import { MicrofrontendRouterService } from '../../services/mfeRouterService';
import { MonitoringService } from '../../services/monitoringService';
import { NavigationService } from '../../services/navigationService';
import { PortalOnboardingService } from '../../services/PoralOnboardingService';
import { RoutesService } from '../../services/RoutesService';
import { ScopeService } from '../../services/scope';
import { ActionService } from '../../services/action/ActionService';
import { AccessControlService } from '../../services/accessControlService';
import { EntitlementsService } from '../../services/entitlementsService';
import { ServiceWorkerService } from '../../services/serviceWorkerService';
import { SessionServiceFactory } from '../../services/session/SessionServiceFactory';
import { LoginServiceFactory } from '../../services/session/loginService/LoginServiceFactory';
import { LogoutServiceFactory } from '../../services/session/logoutService/LogoutServiceFactory';
import RefreshTokenServiceNative from '../../services/session/refreshTokenService/RefreshTokenServiceNative';
import RefreshTokenServiceWeb from '../../services/session/refreshTokenService/RefreshTokenServiceWeb';
import { StateParamHandlerService } from '../../services/stateParamHandlerService';
import {
  ISupportSessionService,
  SupportSessionService
} from '../../services/supportSession';
import { TenantHandlerService } from '../../services/tenantHandler';
import { ThemeService } from '../../services/themeService';
import { URLService } from '../../services/URLService';
import { UserOnboardingServiceFactory } from '../../services/userOnboardingService/UserOnboardingServiceFactory';
import { UserServiceFactory } from '../../services/userService/UserServiceFactory';
import WebServiceRouting from '../../services/webServiceRouting';
import { RepositoriesType } from '../repositories/RepositoriesType';
import { ServicesType } from './ServicesType';
import {
  CommonsInitializeType,
  SetServiceDependencies
} from '../startup/types';
import { LoggerService } from '../../services/loggerService';
import { RouteStageService } from '../../services/RouteStageService';
import { CriterionService } from '../../services/criterionService';
import { EnvironmentVariablesService } from '../../services/environmentVariablesService';
import ConfigurationManager from '../configuration/ConfigurationManager';

export type ServicesSetDependenciesParams = {
  repositories: RepositoriesType;
};

export default class ServicesInitializer {
  public static async instantiate(
    params: CommonsInitializeType
  ): Promise<ServicesType> {
    const {
      abTesting,
      jWebPlatform,
      stack,
      clientId,
      appName,
      localization,
      analytics,
      accessControl,
      consent,
      isNative,
      theme,
      login,
      fallbackConfiguration,
      logger,
      defaultLayoutKey,
      backgroundTasks,
      routes,
      featureFlags,
      monitoring,
      breadcrumb,
      serviceWorker,
      userActivity,
      idleLogout,
      urlService,
      grants,
      grantsHistory,
      layouts,
      graphql,
      applicationContext,
      userOnboarding,
      onboarding,
      criterions,
      entitlements,
      environmentVariables
    } = params;

    const services = {} as ServicesType;
    const isShellPortal = !!clientId;

    // Contextless services

    services.graphQLService = GraphQLServiceFactory.createService({
      graphql,
      isShellPortal,
      jWebPlatform
    });

    services.URLService = new URLService(urlService);

    // TODO: I think the setDependency for this service is missing.
    // Also the missing information should be provided in the constructor
    services.layoutsService = new LayoutsService({
      defaultLayoutKey,
      layoutList: layouts
    });

    services.eventService = EventServiceFactory.createService({ isNative });

    // Store service - currently only web implementation
    services.storeService = container.resolve(WebStoreService);

    // Fallback service
    services.fallbackService = new FallbackService({
      configuration: fallbackConfiguration
    });

    services.authProviderService = new AuthProviderService();

    services.tenantHandlerService = container.resolve(TenantHandlerService);

    // SUPPORT SESSION INITIALIZER
    const supportSessionService: ISupportSessionService = container.resolve(
      SupportSessionService
    );

    services.supportSessionService = supportSessionService;

    services.abTestingService = new ABTestingService(abTesting);

    services.authTokenService = container.resolve(AuthTokenService);

    services.breadcrumbService = new BreadcrumbService(breadcrumb);

    services.backgroundTaskService = new BackgroundTaskService();
    services.assetLoaderService = new SystemJSAssetLoaderService();

    services.backgroundTaskManagerService = new BackgroundTaskManagerService(
      backgroundTasks
    );

    services.criterionService = new CriterionService({ criterions });

    services.localizationService = new LocalizationService({
      localization
    });

    services.localizationTranslatorService = new LocalizationTranslatorService({
      localization
    });

    services.localizationTranslatorService = new LocalizationTranslatorService({
      localization
    });

    services.loggerService = new LoggerService(logger);

    services.featureFlagService = new LaunchDarklyFeatureFlagService(
      featureFlags
    );

    services.monitoringService = new MonitoringService(monitoring);

    services.navigationService = container.resolve(NavigationService);

    services.routesService = new RoutesService(routes);

    services.routeStageService = new RouteStageService({
      stages: onboarding?.stages
    });

    services.scopeService = new ScopeService();

    services.actionService = new ActionService();

    services.accessControlService = new AccessControlService(accessControl);

    services.entitlementsService = new EntitlementsService(entitlements);

    services.themeService = new ThemeService(theme);

    services.consentService = new ConsentService(consent);

    services.userService = UserServiceFactory.createService({
      authenticationProvider: login.authenticationProvider
    });

    services.webServiceRouting = new WebServiceRouting();

    services.applicationService = ApplicationServiceFactory.createService({
      authenticationProvider: login.authenticationProvider,
      portalStack: stack,
      clientId,
      appName,
      applicationContext
    });

    services.userOnboardingService = UserOnboardingServiceFactory.createService(
      {
        authenticationProvider: login.authenticationProvider,
        userOnboarding
      }
    );

    container.registerInstance(
      'ApplicationService',
      services.applicationService
    );

    services.loginService = LoginServiceFactory.createService({ isNative });
    services.sessionService = SessionServiceFactory.createService({ isNative });

    // Analytics Service
    if (analytics.enabled) {
      services.analyticsService = new AnalyticsService(analytics);
    }

    // Logout Service
    services.logoutService = LogoutServiceFactory.createService({
      authenticationProvider: login.authenticationProvider,
      supportSessionService: services.supportSessionService
    });

    services.mfeRouterService = new MicrofrontendRouterService();
    services.stateParamHandlerService = new StateParamHandlerService();
    services.serviceWorkerService = new ServiceWorkerService(serviceWorker);
    services.idleService = new IdleService(userActivity);
    services.idleLogoutService = new IdleLogoutService(idleLogout);
    services.grantService = new GrantService(grants);
    services.grantHistoryService = new GrantHistoryService(grantsHistory);
    services.portalOnboardingService = new PortalOnboardingService();
    services.environmentVariablesService = new EnvironmentVariablesService(
      environmentVariables
    );

    return services;
  }

  public static async resolveDependencies({
    services,
    repositories,
    clients
  }: SetServiceDependencies): Promise<ServicesType> {
    const defaultDependencies: SetServiceDependencies = {
      services: services,
      repositories,
      clients
    };

    if (!services) {
      throw new Error('Services must be provided to set dependencies.');
    }
    services.analyticsService?.setDependencies(defaultDependencies);
    services.authProviderService?.setDependencies(defaultDependencies);

    services.breadcrumbService?.setDependencies(defaultDependencies);
    services.featureFlagService?.setDependencies(defaultDependencies);
    services.scopeService?.setDependencies(defaultDependencies);
    services.actionService?.setDependencies(defaultDependencies);
    services.accessControlService?.setDependencies(defaultDependencies);
    services.entitlementsService?.setDependencies(defaultDependencies);
    services.sessionService?.setDependencies(defaultDependencies);
    services.routesService.setDependencies(defaultDependencies);
    services.mfeRouterService?.setDependencies(defaultDependencies);
    services.loginService?.setDependencies(defaultDependencies);
    services.logoutService?.setDependencies(defaultDependencies);
    services.tenantHandlerService?.setDependencies(defaultDependencies);
    services.criterionService?.setDependencies(defaultDependencies);
    services.userOnboardingService?.setDependencies(defaultDependencies);
    services.userService?.setDependencies(defaultDependencies);
    services.webServiceRouting?.setDependencies(defaultDependencies);
    services.consentService?.setDependencies(defaultDependencies);
    services.localizationTranslatorService?.setDependencies(
      defaultDependencies
    );
    services.localizationService?.setDependencies(defaultDependencies);
    services.loggerService?.setDependencies(defaultDependencies);
    services.navigationService?.setDependencies(defaultDependencies);
    services.serviceWorkerService?.setDependencies(defaultDependencies);
    services.backgroundTaskManagerService?.setDependencies(defaultDependencies);
    services.fallbackService?.setDependencies(defaultDependencies);
    services.grantService?.setDependencies(defaultDependencies);
    services.grantHistoryService?.setDependencies(defaultDependencies);
    services.stateParamHandlerService?.setDependencies(defaultDependencies);
    services.portalOnboardingService?.setDependencies(defaultDependencies);
    services.routeStageService?.setDependencies(defaultDependencies);
    services.environmentVariablesService?.setDependencies(defaultDependencies);

    return services;
  }

  // It only registers the default resource in case there is no custom override.
  private static _registerIfNotOverriden(key: string, service): void {
    if (!container.isRegistered(key)) {
      container.registerSingleton(key, service);
    }
  }

  public static registerOverridableSingletons(): void {
    this._registerIfNotOverriden(
      'IGetAccessTokenService',
      GetAccessTokenService
    );
    if (!container.isRegistered('IRefreshTokenService')) {
      container.register('IRefreshTokenService', {
        useFactory: () => {
          const isNative = ConfigurationManager.getProps('isNative');
          return isNative
            ? container.resolve(RefreshTokenServiceNative)
            : container.resolve(RefreshTokenServiceWeb);
        }
      });
    }
  }
}
